import { useCallback, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { compressImage } from '../lib/image';

interface Props {
  onCapture: (imageDataUrl: string) => void;
}

export default function CameraCapture({ onCapture }: Props) {
  const webcamRef = useRef<Webcam>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [ready, setReady] = useState(false);
  const [working, setWorking] = useState(false);

  const handleRaw = async (raw: string) => {
    setWorking(true);
    try {
      const compressed = await compressImage(raw, 1280, 0.85);
      onCapture(compressed);
    } catch {
      // Fall back to the raw image if canvas decode fails.
      onCapture(raw);
    } finally {
      setWorking(false);
    }
  };

  const capture = useCallback(() => {
    const image = webcamRef.current?.getScreenshot({ width: 1600, height: 1200 });
    if (image) handleRaw(image);
  }, []);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') handleRaw(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-3xl bg-ink/5 aspect-[4/3]">
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode: { ideal: 'environment' } }}
          onUserMedia={() => setReady(true)}
          onUserMediaError={() => setReady(false)}
          className="w-full h-full object-cover"
        />
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center text-ink/60">
            Allow camera, or upload below
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <button
          onClick={capture}
          disabled={!ready || working}
          className="btn-primary flex-1 disabled:opacity-50"
        >
          {working ? 'Tidying photo…' : '📸 Snap it'}
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={working}
          className="btn-ghost"
        >
          Upload
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}
